import React from 'react';
import { useYearContext } from '../../contexts/YearContext';

interface PDFReportGeneratorProps {
  companyId?: number;
  onGenerate?: () => void;
}

export function PDFReportGenerator({ companyId, onGenerate }: PDFReportGeneratorProps) {
  const { selectedYear, currentCompany } = useYearContext();
  const [isGenerating, setIsGenerating] = React.useState(false);

  const company_id = companyId || currentCompany?.id;
  const reporting_year = selectedYear || new Date().getFullYear();

  const generatePDF = async () => {
    if (!company_id) {
      alert('Please select a company first');
      return;
    }

    try {
      setIsGenerating(true);
      if (onGenerate) onGenerate();

      const API_BASE =
        (import.meta as any).env?.VITE_API_BASE_URL ||
        `${window.location.protocol}//${window.location.hostname}:9000`;
      const token = localStorage.getItem('access_token');
      const response = await fetch(
        `${API_BASE}/api/reports/pdf/${company_id}?reporting_year=${reporting_year}`,
        {
          method: 'GET',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        let detail = 'Failed to generate PDF';
        try {
          const err = await response.json();
          detail = err.detail || detail;
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `GHG_Inventory_${reporting_year}.pdf`;

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error generating PDF:', error);
      alert(error.message || 'Error generating PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <button
      onClick={generatePDF}
      disabled={isGenerating || !company_id}
      className={`inline-flex items-center px-4 py-2 rounded-md transition-colors ${
        isGenerating || !company_id
          ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
          : 'bg-blue-600 text-white hover:bg-blue-700'
      }`}
    >
      {isGenerating ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          Generating PDF...
        </>
      ) : (
        <>
          <i data-feather="download" className="w-4 h-4 mr-2"></i>
          Download PDF Inventory
        </>
      )}
    </button>
  );
}
